import {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import NoteService from "@services/api/note";
import ReportService from "@services/api/report";
import { UserContext } from "@contexts/User";
import { generateUUID } from "@utils/helpers";
import reportGraphqlInstance from "@services/api/report.graphql";
import { startOfDay, subDays } from "date-fns";
import { ability } from "ability/ability";
import { CUSTOM_USER_ROLES, USER_ROLES } from "@utils/consts";
import { useProfile } from "./profile";
import noteGraphqlInstance from "@services/api/note.graphql";
import customReportGraphqlInstance from "@services/api/customReport.graphql";
import processGraphqlInstance from "@services/api/process.graphql";

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const useNoteCreate = () => {
  const { profile: currentProfile } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState(null);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const createNote = useCallback(
    async (
      accountId,
      userId,
      newNote,
      type,
      cb,
      pinned = false,
      journalTypeId = null,
      processTaskId = null,
      processConfigId = null,
    ) => {
      if (!accountId || !userId) return;
      setIsLoading(true);
      const noteResponse = await NoteService.createNote(accountId, {
        id: generateUUID(),
        accountId,
        userId,
        note: newNote,
        type,
        creatorName: currentProfile.name,
        pinned,
        journalTypeId: journalTypeId ?? "f5b4059f-bf6e-4829-8e7d-04ab1b2d031b",
        processTaskId,
        processConfigId,
      });

      if (!isUnmounted.current) {
        setNote(noteResponse);
        setIsLoading(false);
      }

      // Always notify the caller, even if the component already unmounted —
      // otherwise a caller waiting to attach content to this note (e.g. a
      // video-call note box closed right after creation resolves) never
      // learns the created note's id and the empty note is orphaned.
      if (cb && typeof cb === "function") {
        cb(noteResponse);
      }
    },
    [currentProfile],
  );

  return [createNote, { isLoading, note }];
};

export const useNoteJournalTypeUpdate = () => {
  const updateNoteJournalType = useCallback(async (note, journalTypeId, cb) => {
    if (!note?.id || !note?.accountId || !journalTypeId) return;

    const noteResponse = await NoteService.updateNote(
      note.accountId,
      note.id,
      {
        id: note.id,
        accountId: note.accountId,
        userId: note.userId,
        note: note.note,
        type: note.type,
        creatorName: note.creatorName,
        pinned: note.pinned,
        isPublic: note.isPublic,
        journalTypeId,
      },
    );

    if (cb && typeof cb === "function") {
      cb(noteResponse);
    }
  }, []);

  return [updateNoteJournalType];
};

export const useNoteUpdate = () => {
  const { profile: currentProfile } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState(null);
  const isUnmounted = useRef();

  useEffect(() => {
    isUnmounted.current = false;
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  const updateNote = useCallback(
    async (id, accountId, userId, newNote, type, pinned, isPublic, cb) => {
      if (!id || !accountId || !userId) return;
      setIsLoading(true);
      const noteResponse = await noteGraphqlInstance.updateNoteNote({
        id,
        accountId,
        userId,
        note: newNote,
        creatorName: currentProfile.name,
        type,
        pinned,
        isPublic,
      });

      if (!isUnmounted.current) {
        setNote(noteResponse);
        setIsLoading(false);

        if (cb && typeof cb === "function") {
          cb();
        }
      }
    },
    [currentProfile],
  );

  return [updateNote, { isLoading, note }];
};

export const useNoteDelete = () => {
  const { profile: currentProfile } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const deleteNote = useCallback(
    async (accountId, noteId, cb) => {
      if (!accountId || !noteId) return;
      setIsLoading(true);
      await NoteService.deleteNote(accountId, noteId);

      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") {
          cb();
        }
      }
    },
    [currentProfile],
  );

  return [deleteNote, { isLoading }];
};

export const useNotesList = (accountId, userId, customRolesId = "") => {
  const [notes, setNotes] = useState(null);
  const [reports, setReports] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isUnmounted = useRef();

  useEffect(() => {
    (async () => {
      if (!accountId || !userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const canAccessReports = ability.can(
        CUSTOM_USER_ROLES[USER_ROLES.COUNSELLOR_ROLE].REPORTS,
        USER_ROLES.COUNSELLOR_ROLE,
      );
      const canAccessNotes = ability.can(
        CUSTOM_USER_ROLES[USER_ROLES.COUNSELLOR_ROLE].NOTES,
        USER_ROLES.COUNSELLOR_ROLE,
      );

      const notesResponse = canAccessNotes
        ? await NoteService.getNotesByUserId(accountId, userId, customRolesId)
        : [];
      const sortedNotes = notesResponse
        .filter((note) => note.note)
        .sort((a, b) => b.createdAt - a.createdAt);

      const reportsResponse = canAccessReports
        ? await ReportService.getAllReports(accountId, userId, customRolesId)
        : [];
      const sortReport = reportsResponse.filter(
        (report) => report.userId === userId,
      );

      if (!isUnmounted.current) {
        setReports(sortReport || []);
        setNotes(sortedNotes || []);
        setIsLoading(false);
      }

      return () => {
        isUnmounted.current = true;
      };
    })();
  }, [accountId, userId]);

  return useMemo(() => {
    const result = notes ?? [];
    const report = reports ?? [];
    return [result, report, { isLoading }];
  }, [notes, reports, isLoading]);
};

export const useNotesListN = (
  profileId = null,
  journalTypeId = null,
  profileData = null,
) => {
  const { profile: currentProfile } = useContext(UserContext);
  if (!isUuid(journalTypeId)) {
    journalTypeId = null;
  }
  const profile = useProfile(
    profileData ? undefined : (profileId ?? undefined),
  );

  const isStudent = currentProfile?.type === USER_ROLES.STUDENT_ROLE;
  if (isStudent) {
    return [
      [],
      [],
      [],
      { isLoading: false, refetch: () => {} },
      () => {},
      true,
    ];
  }

  const targetUserId = profileData?.userId ?? profile?.userId;

  const [notes, setNotes] = useState([]);
  const [reports, setReports] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  // Starts true (not false) — the mount effect below always kicks off a
  // fetch, so the initial render should already show the loader instead of
  // a "no reports" flash before that fetch has had a chance to run.
  const [isLoading, setIsLoading] = useState(true);
  const [dataOver, setDataOver] = useState(false);

  const initialStart = useMemo(
    () => startOfDay(subDays(Date.now(), 30)).getTime(),
    [],
  );

  const cursorStartRef = useRef(initialStart);
  const cursorEndRef = useRef(Date.now());
  const unmountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const safeJsonParse = (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const parseReports = useCallback(
    (list = []) =>
      list.map((r) => ({ ...r, data: r?.data ? safeJsonParse(r.data) : {} })),
    [],
  );

  const dedupeAppend = useCallback((prevList, incomingList, keyFn) => {
    const map = new Map(prevList.map((it) => [keyFn(it), it]));
    for (const it of incomingList || []) {
      map.set(keyFn(it), it);
    }
    return Array.from(map.values());
  }, []);

  const makeKey = (it) => `${it.__typename ?? it.type ?? "item"}-${it.id}`;

  const fetchWindow = useCallback(async (start, end, userId, journalTypeId) => {
    return reportGraphqlInstance.getAllReportsAndNotesByCustomRoleId(
      start,
      end,
      userId,
      journalTypeId,
    );
  }, []);

  const fetchReportsAndNotes = useCallback(async () => {
    if (profileId && !targetUserId) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setIsLoading(true);

    const start = initialStart;
    const end = Date.now();

    cursorStartRef.current = start;
    cursorEndRef.current = end;

    const resp = await fetchWindow(start, end, targetUserId, journalTypeId);

    if (unmountedRef.current || requestId !== requestIdRef.current) return;

    if (!resp) {
      setIsLoading(false);
      return;
    }

    const nextStart = resp?.responseDate ? Number(resp.responseDate) : start;
    const over = Boolean(resp?.dataOver);

    const nextReports = parseReports(resp?.reports || []);
    const nextNotes = resp?.notes || [];
    const nextCustom = resp?.customReports || [];

    setReports(nextReports);
    setNotes(nextNotes);
    setCustomReports(nextCustom);
    cursorStartRef.current = nextStart;
    setDataOver(over);
    setIsLoading(false);
  }, [
    profileId,
    targetUserId,
    initialStart,
    fetchWindow,
    parseReports,
    journalTypeId,
  ]);

  useEffect(() => {
    unmountedRef.current = false;
    fetchReportsAndNotes();

    return () => {
      unmountedRef.current = true;
    };
  }, [fetchReportsAndNotes]);

  const loadMoreDateResponse = useCallback(async () => {
    if (dataOver || isLoading) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const currentStart = cursorStartRef.current;
    const newEnd = startOfDay(new Date(currentStart)).getTime() - 1;
    const newStart = startOfDay(subDays(new Date(currentStart), 30)).getTime();

    setIsLoading(true);

    const resp = await fetchWindow(
      newStart,
      newEnd,
      targetUserId,
      journalTypeId,
    );

    if (unmountedRef.current || requestId !== requestIdRef.current) return;

    if (!resp) {
      setIsLoading(false);
      return;
    }

    const over = Boolean(resp?.dataOver);
    const nextStart = resp?.responseDate ? Number(resp.responseDate) : newStart;

    const moreReports = parseReports(resp?.reports || []);
    const moreNotes = resp?.notes || [];
    const moreCustom = resp?.customReports || [];

    cursorStartRef.current = nextStart;
    cursorEndRef.current = newEnd;

    setReports((prev) => dedupeAppend(prev, moreReports, makeKey));
    setNotes((prev) => dedupeAppend(prev, moreNotes, makeKey));
    setCustomReports((prev) => dedupeAppend(prev, moreCustom, makeKey));
    setDataOver(over);
    setIsLoading(false);
  }, [
    dataOver,
    isLoading,
    fetchWindow,
    targetUserId,
    parseReports,
    dedupeAppend,
    journalTypeId,
  ]);

  return useMemo(
    () => [
      notes,
      reports,
      customReports,
      { isLoading, refetch: fetchReportsAndNotes },
      loadMoreDateResponse,
      dataOver,
    ],
    [
      notes,
      reports,
      customReports,
      isLoading,
      fetchReportsAndNotes,
      loadMoreDateResponse,
      dataOver,
    ],
  );
};

export const useNoteLast = (accountId, userId, customRolesId = "") => {
  const [note, setNote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isUnmounted = useRef();

  useEffect(() => {
    (async () => {
      if (!accountId || !userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const notesResponse = await NoteService.getLastNoteByUserId(
        accountId,
        userId,
        customRolesId,
      );

      if (!isUnmounted.current) {
        setNote(notesResponse[0]);
        setIsLoading(false);
      }

      return () => {
        isUnmounted.current = true;
      };
    })();
  }, [accountId, userId]);

  return useMemo(() => {
    const result = note ?? null;

    return [result, { isLoading }];
  }, [note, isLoading]);
};

export const useNote = (accountId, id) => {
  const [note, setNote] = useState(null);
  const isUnmounted = useRef();

  useEffect(() => {
    (async () => {
      if (!accountId || !id) return;
      const notesResponse = await noteGraphqlInstance.getNoteById(
        accountId,
        id,
      );

      if (!isUnmounted.current) {
        setNote(notesResponse);
      }
      return () => {
        isUnmounted.current = true;
      };
    })();
  }, [accountId, id]);

  return useMemo(() => {
    if (!note) {
      return null;
    }

    return note;
  }, [note]);
};

export const useReport = (accountId, id) => {
  const [report, setReport] = useState(null);
  const isUnmounted = useRef();

  useEffect(() => {
    (async () => {
      if (!accountId || !id) return;
      const reportResponse =
        await reportGraphqlInstance.getReportByIdAndAccountId(id, accountId);

      if (!isUnmounted.current) {
        setReport({
          ...reportResponse,
          data: reportResponse.data ? JSON.parse(reportResponse.data) : {},
        });
      }
      return () => {
        isUnmounted.current = true;
      };
    })();
  }, [accountId, id]);

  return useMemo(() => {
    if (!report) {
      return null;
    }

    return report;
  }, [report]);
};

export const useCustomReport = (accountId, id, { refetch = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [customReport, setCustomReport] = useState(null);
  const isUnmounted = useRef();

  useEffect(() => {
    (async () => {
      if (!accountId || !id) return;
      const customReportResponse =
        await reportGraphqlInstance.getCustomReportDataByIdAndAccountId(
          id,
          accountId,
        );

      if (!isUnmounted.current && customReportResponse) {
        setCustomReport({
          ...customReportResponse,
          data: customReportResponse?.data
            ? JSON.parse(customReportResponse.data)
            : {},
        });
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
      return () => {
        isUnmounted.current = true;
      };
    })();
  }, [accountId, id, refetch]);

  return useMemo(() => {
    return { customReport, isLoading };
  }, [customReport, isLoading]);
};

export const useReportDelete = () => {
  const { profile: currentProfile } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const deleteReport = useCallback(
    async (accountId, reportId, cb) => {
      if (!accountId || !reportId) return;
      setIsLoading(true);
      await reportGraphqlInstance.removeReportById(reportId);

      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") {
          cb();
        }
      }
    },
    [currentProfile],
  );

  return [deleteReport, { isLoading }];
};

export const useCustomReportDelete = () => {
  const { profile: currentProfile } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const deleteCustomReport = useCallback(
    async (accountId, reportId, cb) => {
      if (!accountId || !reportId) return;
      setIsLoading(true);
      await reportGraphqlInstance.removeCustomReportById(reportId);
      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") {
          cb();
        }
      }
    },
    [currentProfile],
  );

  return [deleteCustomReport, { isLoading }];
};

export const useUpdateReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const updateReport = useCallback(async (data, cb) => {
    if (!data) return;
    setIsLoading(true);
    const reportResponse = await reportGraphqlInstance.updateReport(data);

    if (!isUnmounted.current) {
      setReport(reportResponse);
      setIsLoading(false);
      if (cb && typeof cb === "function") {
        cb();
      }
    }
  }, []);

  return [updateReport, { isLoading, report }];
};

export const useCustomReportChangeLogs = (
  customReportId,
  { refetch = false } = {},
) => {
  const [changeLogs, setChangeLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isUnmounted = useRef(false);

  useEffect(() => {
    isUnmounted.current = false;

    (async () => {
      if (!customReportId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const changeLogsResponse =
        await customReportGraphqlInstance.getCustomReportReportArchivesByCustomReportId(
          customReportId,
        );

      if (!isUnmounted.current) {
        setChangeLogs(changeLogsResponse || []);
        setIsLoading(false);
      }
    })();

    return () => {
      isUnmounted.current = true;
    };
  }, [customReportId, refetch]);

  return useMemo(() => {
    const result = changeLogs ?? [];

    return [result, { isLoading }];
  }, [changeLogs, isLoading]);
};

export const useSignCustomReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const signCustomReport = useCallback(async (id, confirmedName, cb) => {
    if (!id || !confirmedName) return;
    setIsLoading(true);
    try {
      const result = await customReportGraphqlInstance.signCustomReport(
        id,
        confirmedName,
      );
      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") cb(result, null);
      }
    } catch (err) {
      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") cb(null, err);
      }
    }
  }, []);

  return [signCustomReport, { isLoading }];
};

export const useUnlockCustomReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const unlockCustomReport = useCallback(async (id, reason, cb) => {
    if (!id || !reason) return;
    setIsLoading(true);
    try {
      const result = await customReportGraphqlInstance.unlockCustomReport(
        id,
        reason,
      );
      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") cb(result, null);
      }
    } catch (err) {
      if (!isUnmounted.current) {
        setIsLoading(false);
        if (cb && typeof cb === "function") cb(null, err);
      }
    }
  }, []);

  return [unlockCustomReport, { isLoading }];
};

export const useUpdateCustomReportAllowGuestEditing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isUnmounted = useRef();

  useEffect(
    () => () => {
      isUnmounted.current = true;
    },
    [],
  );

  const updateCustomReportAllowGuestEditing = useCallback(
    async (id, allowGuestEditing, cb) => {
      if (!id) return;
      setIsLoading(true);
      try {
        const result =
          await customReportGraphqlInstance.updateCustomReportAllowGuestEditing(
            id,
            allowGuestEditing,
          );
        if (!isUnmounted.current) {
          setIsLoading(false);
          if (cb && typeof cb === "function") cb(result, null);
        }
      } catch (err) {
        if (!isUnmounted.current) {
          setIsLoading(false);
          if (cb && typeof cb === "function") cb(null, err);
        }
      }
    },
    [],
  );

  return [updateCustomReportAllowGuestEditing, { isLoading }];
};

export const useReportDocuments = (profileId, accountId, journalTypeId) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isUnmounted = useRef(false);

  useEffect(() => {
    isUnmounted.current = false;

    (async () => {
      if (
        !profileId ||
        !accountId ||
        !journalTypeId ||
        !isUuid(journalTypeId)
      ) {
        setIsLoading(false);
        setDocuments([]);
        return;
      }

      setIsLoading(true);
      const documentsResponse =
        await reportGraphqlInstance.getReportDocumentsByProfileId(
          profileId,
          accountId,
          journalTypeId,
        );

      if (!isUnmounted.current) {
        setDocuments(documentsResponse || []);
        setIsLoading(false);
      }
    })();

    return () => {
      isUnmounted.current = true;
    };
  }, [profileId, accountId, journalTypeId]);

  const refetch = useCallback(() => {
    setIsLoading(true);
    reportGraphqlInstance
      .getReportDocumentsByProfileId(profileId, accountId, journalTypeId)
      .then((documentsResponse) => {
        if (isUnmounted.current) return;
        setDocuments(documentsResponse || []);
        setIsLoading(false);
      });
  }, [profileId, accountId, journalTypeId]);

  return useMemo(() => {
    const result = documents ?? [];

    return [result, { isLoading, refetch }];
  }, [documents, isLoading, refetch]);
};

export const useProcessDocuments = (profileId, accountId, processConfigId) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isUnmounted = useRef(false);

  useEffect(() => {
    isUnmounted.current = false;

    (async () => {
      if (!profileId || !accountId || !processConfigId) {
        setIsLoading(false);
        setDocuments([]);
        return;
      }

      setIsLoading(true);
      const documentsResponse =
        await processGraphqlInstance.getProcessDocumentsByProfileId(
          profileId,
          accountId,
          processConfigId,
        );

      if (!isUnmounted.current) {
        setDocuments(documentsResponse || []);
        setIsLoading(false);
      }
    })();

    return () => {
      isUnmounted.current = true;
    };
  }, [profileId, accountId, processConfigId]);

  const refetch = useCallback(() => {
    setIsLoading(true);
    processGraphqlInstance
      .getProcessDocumentsByProfileId(profileId, accountId, processConfigId)
      .then((documentsResponse) => {
        if (isUnmounted.current) return;
        setDocuments(documentsResponse || []);
        setIsLoading(false);
      });
  }, [profileId, accountId, processConfigId]);

  return useMemo(() => {
    const result = documents ?? [];

    return [result, { isLoading, refetch }];
  }, [documents, isLoading, refetch]);
};
